/**
 * Copyright 2022-present terrestris GmbH & Co. KG
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 * list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 * this list of conditions and the following disclaimer in the documentation
 * and/or other materials provided with the distribution.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 */
import React from 'react';

import {
  faAngleDoubleRight
} from '@fortawesome/free-solid-svg-icons';
import {
  FontAwesomeIcon
} from '@fortawesome/react-fontawesome';

import {
  Drawer
} from 'antd';
import {
  DrawerProps
} from 'antd/lib/drawer';

import {
  useTranslation
} from 'react-i18next';

import {
  useMap
} from '@terrestris/react-util';

const BasicLayerTree = React.lazy(() => import('../LayerTree/LayerTree'));

import {
  useAppDispatch
} from '../../hooks/useAppDispatch';
import {
  useAppSelector
} from '../../hooks/useAppSelector';
import {
  toggleVisibility
} from '../../store/drawer';

import FeatureInfo from '../FeatureInfoDrawer/FeatureInfo';

import './SideDrawer.less';

export const SideDrawer: React.FC<Partial<DrawerProps>> = (props): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const visible = useAppSelector((state) => state.drawer.visible);
  const featureInfoActive = useAppSelector((state) => state.featureInfo.active);

  const {
    t
  } = useTranslation();
  const map = useMap();

  const toggleDrawer = () => {
    dispatch(toggleVisibility());
  };

  return (
    <Drawer
      title={t('Drawer.title')}
      className="side-drawer"
      placement="right"
      onClose={toggleDrawer}
      width={400}
      open={visible}
      mask={false}
      getContainer={'#app'}
      forceRender // required so FeatureInfo can be active
      closeIcon={
        <FontAwesomeIcon
          icon={faAngleDoubleRight}
          size='lg'
        />
      }
      {...props}
    >
      <React.Suspense
        fallback={null}
      >
        <BasicLayerTree />
      </React.Suspense>
      {
        <div className="ant-drawer-header featureinfo-header">
          <div className="ant-drawer-title">
            Objektinformationen
          </div>
        </div>
      }
      {
        map &&
        <FeatureInfo
          enabled={featureInfoActive}
        />
      }
    </Drawer>
  );
};

export default SideDrawer;
